/**
 * @file auth.api.js
 * @module AuthAPI
 * @description Authentication related API wrapper (login, password recovery, validation).
 * Methods return Axios promises from axiosInstance().
 */

import { axiosInstance } from "./axios"

export const AuthAPI = {
    /**
     * Logs in a user with provided credentials.
     * @param {Object} data - Login payload (email/password or similar).
     * @returns {Promise} Axios promise resolving to authentication result (tokens, user data).
     */
    login: (data) => {
        return axiosInstance().post(`/auth/users`, data);
    },

    /**
     * Validate whether an email is acceptable/available.
     * @param {string} email - Email to validate.
     * @returns {Promise} Axios promise resolving to validation result.
     */
    validateEmail: (email) => {
        return axiosInstance().get(`/validation/email/${email}`);
    },

    /**
     * Verify if a customer exists for registration flows.
     * @param {string} email - Customer email to verify.
     * @param {string} owner - Owner identifier.
     * @param {string} type - Customer type.
     * @returns {Promise} Axios promise resolving to verification result.
     */
    verifyCustomer: (email, owner, type) => {
        return axiosInstance().post(`/customer-exists`, { email: email, owner: owner, type });
    },

    /**
     * Initiate forgot password process for an email.
     * @param {string} email - Email associated with the account.
     * @returns {Promise} Axios promise resolving to operation result.
     */
    forgotPassword: (email) => {
        return axiosInstance().post(`/auth/users/forgot-password`, { email: email })
    },

    /**
     * Restore a user's password using token/operation details.
     * @param {string} userId - Identifier of the user to restore password for.
     * @param {string} userType - Type of the user.
     * @param {Object} data - New password payload.
     * @param {string} operation - Restore operation identifier.
     * @param {string} token - Restore token.
     * @returns {Promise} Axios promise resolving to the restore result.
     */
    restorePassword: (userId, userType, data, operation, token = "") => {
        return axiosInstance().put(`/auth/users/${userId}/restore-password?userType=${userType}&operation=${operation}&token=${token}`, data)
    },

    /**
     * Refresh the session token of the current user.
     * @param {string} token - Current session token.
     * @returns {Promise} Axios promise resolving to the refreshed session.
     */
    refreshToken: (token) => {
        return axiosInstance(token).post(`/auth/refresh`)
    },

    /**
     * Check if an email is unique in the system.
     * @param {string} email - Email to check.
     * @returns {Promise} Axios promise resolving to uniqueness boolean/result.
     */
    checkUniqueEmail: (email) => {
        return axiosInstance().post(`/auth/check-unique-email`, { email: email });
    }
}
